import React, { useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Sun,
  User,
  Box,
  Package,
  Download,
} from "lucide-react";
import { isOwnerOrAdminRole } from "../../utils/sampleOrderVisibility";
import {
  downloadOutlookCalendarFile,
  getWeekRange,
  isDateWithinWeek,
} from "../../utils/outlookCalendarExport";

/**
 * Canonical sample order identity fields (companies/{companyId}/sampleOrders/{orderId}):
 * - createdBy (uid string)
 * - createdByName (string)
 * - createdByRole (lowercase string: "customer" | "owner" | "admin" | "enduser")
 */

// Helper: format Date -> "YYYY-MM-DD"
const formatYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse Firestore timestamp / string / Date to "YYYY-MM-DD"
const toYMDFromRaw = (raw) => {
  if (!raw) return null;

  if (raw && typeof raw.toDate === "function") {
    return formatYMD(raw.toDate());
  }

  if (typeof raw === "string") {
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return match[0];
    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) return formatYMD(parsed);
    return null;
  }

  if (raw instanceof Date) {
    return formatYMD(raw);
  }

  return null;
};

const isWeekend = (date) => {
  const d = date.getDay();
  return d === 0 || d === 6;
};

const getBaseRangeForView = (currentDate, viewMode) => {
  const base = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate()
  );

  if (viewMode === "day") return { start: base, end: base };

  if (viewMode === "week") {
    const start = new Date(base);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  }

  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  return { start, end };
};

const getAllDatesInRange = (start, end) => {
  const dates = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const buildMonthGrid = (baseStart) => {
  const year = baseStart.getFullYear();
  const month = baseStart.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startDay = firstOfMonth.getDay();
  const gridStart = new Date(year, month, 1 - startDay);

  const weeks = [];
  let current = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate());

  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};

const deriveItemMeta = (request) => {
  let items = Array.isArray(request.items) ? request.items : [];

  if (!items.length && (request.productId || request.productName)) {
    items = [
      {
        productId: request.productId || null,
        productName: request.productName || null,
        productCode: request.productCode || null,
        quantity: request.quantity || 1,
      },
    ];
  }

  const itemCount = items.length || (request.productId ? 1 : 0);

  const primaryItemName =
    (items[0] && (items[0].productName || items[0].productCode)) ||
    request.productName ||
    "Sample Request";

  const totalQty =
    items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0) ||
    request.quantity ||
    0;

  const extraItemsCount = itemCount > 1 ? itemCount - 1 : 0;

  const itemsTooltip =
    items.length > 0
      ? items
          .map((it, idx) => {
            const name = it.productName || it.productCode || `Item ${idx + 1}`;
            const qty = it.quantity ?? 1;
            return `${qty} x ${name}`;
          })
          .join("\n")
      : "";

  return { itemCount, primaryItemName, totalQty, extraItemsCount, itemsTooltip };
};

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// -------------------------
// Visibility + canonical identity helpers (NO legacy)
// -------------------------
const normId = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.id || v.uid || v.userId || v.value || null;
  return String(v);
};

const normalizeRoleLoose = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

// Enforce canonical createdByRole values ONLY (customer|owner|admin|enduser)
const normalizeCreatedByRole = (role) => {
  const r = normalizeRoleLoose(role);
  if (!r) return null;
  if (r === "customer") return "customer";
  if (r === "owner") return "owner";
  if (r === "admin") return "admin";
  if (r === "enduser") return "enduser";
  return null;
};

const getBestCreatorName = (order) => {
  if (order?.createdByName && String(order.createdByName).trim()) return order.createdByName;
  return null;
};

const isCalendarDeliveryLocked = (request) => {
  const status = String(request?.status || "").trim();
  return ["Follow Up", "Completed", "Converted to Order", "Cancelled"].includes(status);
};

const getCalendarStatusStyles = (request) => {
  const status = String(request?.status || "").trim();
  const isConverted = request?.convertedToOrder === true || status === "Converted to Order";

  if (isConverted) {
    return {
      card: "bg-purple-50 border-purple-200 hover:bg-purple-100 hover:border-purple-300",
      roleChip: "bg-white/70 text-purple-700 border-purple-200",
      statusChip: "bg-purple-100 text-purple-700 border-purple-200",
      statusDot: "bg-purple-400",
    };
  }

  switch (status) {
    case "Pending":
      return {
        card: "bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300",
        roleChip: "bg-white/70 text-amber-800 border-amber-200",
        statusChip: "bg-amber-100 text-amber-700 border-amber-200",
        statusDot: "bg-amber-400",
      };
    case "Approved":
    case "Preparation":
      return {
        card: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300",
        roleChip: "bg-white/70 text-emerald-700 border-emerald-200",
        statusChip: "bg-emerald-100 text-emerald-700 border-emerald-200",
        statusDot: "bg-emerald-400",
      };
    case "Shipped":
      return {
        card: "bg-sky-50 border-sky-200 hover:bg-sky-100 hover:border-sky-300",
        roleChip: "bg-white/70 text-sky-700 border-sky-200",
        statusChip: "bg-sky-100 text-sky-700 border-sky-200",
        statusDot: "bg-sky-400",
      };
    case "Delivered":
      return {
        card: "bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300",
        roleChip: "bg-white/70 text-blue-700 border-blue-200",
        statusChip: "bg-blue-100 text-blue-700 border-blue-200",
        statusDot: "bg-blue-400",
      };
    case "Follow Up":
      return {
        card: "bg-rose-50 border-rose-200 hover:bg-rose-100 hover:border-rose-300",
        roleChip: "bg-white/70 text-rose-700 border-rose-200",
        statusChip: "bg-rose-100 text-rose-700 border-rose-200",
        statusDot: "bg-rose-400",
      };
    case "Completed":
      return {
        card: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300",
        roleChip: "bg-white/70 text-emerald-700 border-emerald-200",
        statusChip: "bg-emerald-100 text-emerald-700 border-emerald-200",
        statusDot: "bg-emerald-400",
      };
    case "Cancelled":
      return {
        card: "bg-slate-100 border-slate-300 hover:bg-slate-200 hover:border-slate-400",
        roleChip: "bg-white/70 text-slate-700 border-slate-300",
        statusChip: "bg-slate-200 text-slate-700 border-slate-300",
        statusDot: "bg-slate-400",
      };
    default:
      return {
        card: "bg-sky-50 border-sky-200 hover:bg-sky-100 hover:border-sky-300",
        roleChip: "bg-white/70 text-sky-700 border-sky-200",
        statusChip: "bg-sky-100 text-sky-700 border-sky-200",
        statusDot: "bg-sky-400",
      };
  }
};

const SampleOrderCalendar = ({
  requests = [],
  isAdmin,
  currentUserId,
  currentUserRole = "",
  currentUserGroupIds = [], // kept for backwards-compat signature (not used)
  currentUserAccountId, // OPTIONAL: supports customer reuse
  onUpdateRequest,
  onSelectRequest,
}) => {
  const [viewMode, setViewMode] = useState("month");
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedRequestId, setDraggedRequestId] = useState(null);

  const isAdminOrOwner = !!isAdmin || isOwnerOrAdminRole(currentUserRole);
  const thisWeekRange = useMemo(() => getWeekRange(new Date()), []);

  // ✅ Canonical visibility (NO requestedBy, NO legacy fallbacks)
  // - Admin/Owner: see all (except hidden)
  // - Non-admin staff: ONLY see mine (createdBy OR assignedToId/assignedTo)
  // - Customer: account match OR createdBy
  const visibleRequests = useMemo(() => {
    const arr = Array.isArray(requests) ? requests : [];
    const me = normId(currentUserId);
    const roleNorm = normalizeRoleLoose(currentUserRole);

    const notHidden = (o) => !o?.hidden;

    // Admin/Owner: all not-hidden
    if (isAdminOrOwner) return arr.filter(notHidden);

    // Non-admin must have user id
    if (!me) return [];

    const getCreatedBy = (o) => normId(o?.createdBy);

    const getAssignedToId = (o) => {
      const v = o?.assignedToId ?? o?.assignedTo ?? null;
      return normId(v);
    };

    // Customer: account match OR createdBy==me
    if (roleNorm === "customer") {
      const acct = normId(currentUserAccountId);
      return arr.filter((o) => {
        if (!notHidden(o)) return false;
        const createdBy = getCreatedBy(o);
        const orderAccountId = normId(o?.accountId);
        return (acct && orderAccountId && orderAccountId === acct) || createdBy === me;
      });
    }

    // Staff endUser: mine only
    return arr.filter((o) => {
      if (!notHidden(o)) return false;
      const createdBy = getCreatedBy(o);
      const assignedToId = getAssignedToId(o);
      return createdBy === me || assignedToId === me;
    });
  }, [requests, currentUserId, currentUserRole, currentUserAccountId, isAdminOrOwner]);

  // Map: ymd -> [requests]
  const requestsByDate = useMemo(() => {
    const map = {};
    visibleRequests.forEach((req) => {
      if (req.hidden) return;
      const rawDate = req.deliveryDate || req.requestedDate;
      const ymd = toYMDFromRaw(rawDate);
      if (!ymd) return;
      if (!map[ymd]) map[ymd] = [];
      map[ymd].push(req);
    });
    return map;
  }, [visibleRequests]);

  const weeklyDeliveryEvents = useMemo(() => {
    return visibleRequests
      .filter((req) => {
        if (req?.hidden) return false;
        const rawDate = req.deliveryDate || req.requestedDate;
        const ymd = toYMDFromRaw(rawDate);
        if (!ymd) return false;
        const date = new Date(`${ymd}T00:00:00`);
        return isDateWithinWeek(date, thisWeekRange);
      })
      .map((req) => {
        const rawDate = req.deliveryDate || req.requestedDate;
        const ymd = toYMDFromRaw(rawDate);
        const start = new Date(`${ymd}T00:00:00`);
        const itemMeta = deriveItemMeta(req);
        const accountName = req.accountName || "No Account";
        const title = `${accountName} delivery`;
        const detailParts = [
          `Sample request: ${req.id || ""}`,
          `Account: ${accountName}`,
          itemMeta.primaryItemName ? `Primary item: ${itemMeta.primaryItemName}` : null,
          itemMeta.totalQty ? `Total quantity: ${itemMeta.totalQty}` : null,
          req.status ? `Status: ${req.status}` : null,
          req.notes ? `Notes: ${req.notes}` : null,
        ].filter(Boolean);

        return {
          uid: `sample-delivery-${req.id}-${ymd}`,
          title,
          start,
          allDay: true,
          description: detailParts.join("\n"),
          url: `${window.location.origin}/app/sample-orders?requestId=${encodeURIComponent(req.id)}`,
        };
      });
  }, [thisWeekRange, visibleRequests]);

  const handleExportWeeklyDeliveries = () => {
    if (weeklyDeliveryEvents.length === 0) {
      window.alert("There are no deliveries scheduled for this week.");
      return;
    }

    const weekLabel = formatYMD(thisWeekRange.start);
    downloadOutlookCalendarFile({
      filename: `supplyhawk-deliveries-${weekLabel}.ics`,
      events: weeklyDeliveryEvents,
    });
  };

  const { start: baseStart, end: baseEnd } = useMemo(
    () => getBaseRangeForView(currentDate, viewMode),
    [currentDate, viewMode]
  );

  const weeks = useMemo(() => {
    if (viewMode === "month") return buildMonthGrid(baseStart);
    const dates = getAllDatesInRange(baseStart, baseEnd);
    return [dates];
  }, [viewMode, baseStart, baseEnd]);

  const rangeLabel = useMemo(() => {
    if (viewMode === "day") {
      return baseStart.toLocaleDateString("en-US", {
        weekday: "short",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    if (viewMode === "week") {
      const startStr = baseStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const endStr = baseEnd.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `${startStr} – ${endStr}`;
    }

    return baseStart.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }, [viewMode, baseStart, baseEnd]);

  const handlePrev = () => {
    const next = new Date(currentDate);
    if (viewMode === "day") next.setDate(next.getDate() - 1);
    else if (viewMode === "week") next.setDate(next.getDate() - 7);
    else next.setMonth(next.getMonth() - 1);
    setCurrentDate(next);
  };

  const handleNext = () => {
    const next = new Date(currentDate);
    if (viewMode === "day") next.setDate(next.getDate() + 1);
    else if (viewMode === "week") next.setDate(next.getDate() + 7);
    else next.setMonth(next.getMonth() + 1);
    setCurrentDate(next);
  };

  const handleToday = () => setCurrentDate(new Date());

  const gridColsClass =
    viewMode === "day" ? "grid-cols-1" : excludeWeekends ? "grid-cols-5" : "grid-cols-7";

  const headerLabels = useMemo(() => {
    if (viewMode === "day") {
      return [baseStart.toLocaleDateString("en-US", { weekday: "short" })];
    }

    if (viewMode === "week") {
      const weekDates = getAllDatesInRange(baseStart, baseEnd);
      const filtered = excludeWeekends ? weekDates.filter((d) => !isWeekend(d)) : weekDates;
      return filtered.map((d) => d.toLocaleDateString("en-US", { weekday: "short" }));
    }

    return excludeWeekends ? weekdayLabels.filter((_, i) => i > 0 && i < 6) : weekdayLabels;
  }, [viewMode, baseStart, baseEnd, excludeWeekends]);

  // Drag & drop handlers (admin-only)
  const handleDragStart = (e, requestId) => {
    if (!isAdminOrOwner) return;
    const req = visibleRequests.find((item) => item?.id === requestId);
    if (!req || isCalendarDeliveryLocked(req)) return;
    setDraggedRequestId(requestId);
    e.dataTransfer.setData("requestId", requestId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    if (!isAdminOrOwner) return;
    setDraggedRequestId(null);
  };

  const handleDayDragOver = (e) => {
    if (!isAdminOrOwner) return;
    e.preventDefault();
    e.currentTarget.classList.add("ring-2", "ring-blue-400");
  };

  const handleDayDragLeave = (e) => {
    if (!isAdminOrOwner) return;
    e.currentTarget.classList.remove("ring-2", "ring-blue-400");
  };

  const handleDayDrop = (e, date) => {
    if (!isAdminOrOwner) return;
    e.preventDefault();
    e.currentTarget.classList.remove("ring-2", "ring-blue-400");
    const requestId = e.dataTransfer.getData("requestId");
    setDraggedRequestId(null);

    if (!requestId || !onUpdateRequest) return;
    const request = visibleRequests.find((item) => item?.id === requestId);
    if (!request || isCalendarDeliveryLocked(request)) return;
    const newYmd = formatYMD(date);

    onUpdateRequest(requestId, { deliveryDate: newYmd });
  };

  return (
    <div className="bg-white rounded-xl px-4 pb-4 pt-3 md:px-5 md:pb-5 md:pt-4 space-y-4">
      {/* Header / controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Sample Orders Calendar</h2>
            <p className="text-xs text-slate-500">{visibleRequests.length} scheduled requests</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleExportWeeklyDeliveries}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" />
            Add this week's Deliveries to Outlook
          </button>

          {/* View toggles */}
          <div className="flex bg-slate-100 rounded-lg p-1 text-xs font-medium">
            <button
              type="button"
              onClick={() => setViewMode("day")}
              className={`px-3 py-1.5 rounded-md ${
                viewMode === "day"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Day
            </button>
            <button
              type="button"
              onClick={() => setViewMode("week")}
              className={`px-3 py-1.5 rounded-md ${
                viewMode === "week"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Week
            </button>
            <button
              type="button"
              onClick={() => setViewMode("month")}
              className={`px-3 py-1.5 rounded-md ${
                viewMode === "month"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Month
            </button>
          </div>

          {/* Exclude weekends toggle */}
          <button
            type="button"
            onClick={() => setExcludeWeekends((prev) => !prev)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${
              excludeWeekends
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Sun className="w-3.5 h-3.5" />
            {excludeWeekends ? "Weekdays only" : "Include weekends"}
          </button>

          {/* Navigation */}
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-1 py-0.5 text-xs">
            <button type="button" onClick={handlePrev} className="p-1 rounded-md hover:bg-slate-100">
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <span className="px-2 font-medium text-slate-700 whitespace-nowrap">{rangeLabel}</span>
            <button type="button" onClick={handleNext} className="p-1 rounded-md hover:bg-slate-100">
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="ml-1 px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="rounded-xl border border-slate-200 overflow-hidden max-h-[70vh] md:max-h-[70vh] flex flex-col">
        {/* Weekday header row */}
        <div
          className={`grid ${gridColsClass} bg-slate-50 border-b border-slate-200 text-[11px] font-medium text-slate-500`}
        >
          {headerLabels.map((label) => (
            <div key={label} className="px-2 py-1.5 text-center uppercase tracking-wide">
              {label}
            </div>
          ))}
        </div>

        {/* Weeks / rows */}
        <div className="flex-1 overflow-y-auto">
          {weeks.map((week, weekIndex) => {
            const daysInRow = excludeWeekends ? week.filter((d) => !isWeekend(d)) : week;

            return (
              <div
                key={weekIndex}
                className={`grid ${gridColsClass} min-h-[90px] border-b border-slate-200 last:border-b-0`}
              >
                {daysInRow.map((date) => {
                  const ymd = formatYMD(date);
                  const isToday = formatYMD(new Date()) === ymd;
                  const inCurrentMonth =
                    viewMode !== "month" || date.getMonth() === baseStart.getMonth();

                  const dayRequests = requestsByDate[ymd] || [];

                  return (
                    <div
                      key={ymd}
                      className={`relative border-r border-slate-200 last:border-r-0 bg-white transition-all ${
                        !inCurrentMonth && viewMode === "month" ? "bg-slate-50 text-slate-400" : ""
                      }`}
                      onDragOver={handleDayDragOver}
                      onDragLeave={handleDayDragLeave}
                      onDrop={(e) => handleDayDrop(e, date)}
                    >
                      {/* Day number */}
                      <div className="flex items-center justify-between px-2 pt-1 pb-0.5 text-[11px]">
                        <div className="flex items-center gap-1">
                          <span
                            className={`font-semibold ${
                              !inCurrentMonth && viewMode === "month"
                                ? "text-slate-400"
                                : "text-slate-700"
                            }`}
                          >
                            {date.getDate()}
                          </span>
                          {isToday && (
                            <span className="text-[9px] px-1 py-0.5 rounded-full bg-blue-600 text-white">
                              Today
                            </span>
                          )}
                        </div>

                        {dayRequests.length > 0 && (
                          <span className="text-[10px] text-slate-400">
                            {dayRequests.length} {dayRequests.length === 1 ? "req" : "reqs"}
                          </span>
                        )}
                      </div>

                      {/* Events in this day */}
                      <div className="px-1.5 pb-1.5 pt-0.5 space-y-1.5">
                        {dayRequests.length === 0 && isAdminOrOwner && (
                          <div className="h-7 text-[10px] text-slate-300 flex items-center justify-center border border-dashed border-slate-200 rounded-md">
                            Drop here
                          </div>
                        )}

                        {dayRequests.map((req) => {
                          const {
                            itemCount,
                            primaryItemName,
                            totalQty,
                            extraItemsCount,
                            itemsTooltip,
                          } = deriveItemMeta(req);

                          const isDragged = draggedRequestId === req.id;
                          const canDragRequest = isAdminOrOwner && !isCalendarDeliveryLocked(req);

                          const creatorName = getBestCreatorName(req);
                          const creatorRole = normalizeCreatedByRole(req?.createdByRole);
                          const statusStyles = getCalendarStatusStyles(req);

                          const tooltipParts = [
                            req.accountName ? `Account: ${req.accountName}` : null,
                            creatorName ? `Created by: ${creatorName}` : null,
                            creatorRole ? `Creator role: ${creatorRole}` : null,
                            itemsTooltip ? `Items:\n${itemsTooltip}` : null,
                          ].filter(Boolean);

                          return (
                            <div
                              key={req.id}
                              draggable={canDragRequest}
                              onDragStart={(e) => handleDragStart(e, req.id)}
                              onDragEnd={handleDragEnd}
                              onClick={() => onSelectRequest && onSelectRequest(req)}
                              className={`group ${
                                canDragRequest ? "cursor-move" : "cursor-pointer"
                              } rounded-md border px-1.5 py-1 text-[10px] leading-tight ${statusStyles.card} transition-colors ${
                                isDragged ? "opacity-60 ring-1 ring-slate-300" : ""
                              }`}
                              title={tooltipParts.join("\n") || "Sample request"}
                            >
                              {/* Account */}
                              <div className="flex items-center gap-1 mb-0.5">
                                <Package className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="font-semibold text-[10px] text-slate-900 truncate">
                                  {req.accountName || "No Account"}
                                </span>

                                {creatorRole && (
                                  <span
                                    className={`ml-auto inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${statusStyles.roleChip}`}
                                  >
                                    {creatorRole}
                                  </span>
                                )}
                              </div>

                              {/* Product + count */}
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <p className="truncate text-[10px] text-slate-700">
                                  {primaryItemName}
                                  {extraItemsCount > 0 && (
                                    <span className="ml-1 text-[9px] text-slate-400">
                                      +{extraItemsCount} more
                                    </span>
                                  )}
                                </p>
                                {itemCount > 0 && (
                                  <span
                                    className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 flex-shrink-0"
                                    title={
                                      itemsTooltip ||
                                      `${itemCount} ${itemCount === 1 ? "item" : "items"}`
                                    }
                                  >
                                    <Box className="w-3 h-3 mr-1" />
                                    {itemCount}
                                  </span>
                                )}
                              </div>

                              {/* Meta */}
                              <div className="flex flex-wrap gap-1 text-[9px] text-slate-500">
                                <div className="inline-flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  <span>{req.assignedToName || "Unassigned"}</span>
                                </div>

                                {creatorName && (
                                  <div className="inline-flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    <span className="truncate">By {creatorName}</span>
                                  </div>
                                )}

                                {totalQty > 0 && (
                                  <div className="inline-flex items-center gap-1">
                                    <Package className="w-3 h-3" />
                                    <span>Qty {totalQty}</span>
                                  </div>
                                )}

                                {req.status && (
                                  <span
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${statusStyles.statusChip}`}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full ${statusStyles.statusDot}`} />
                                    <span>{req.status}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SampleOrderCalendar;
